import * as ko from 'knockout'
import 'knockout.autocomplete/lib/knockout.autocomplete';
import 'knockout.autocomplete/lib/knockout.autocomplete.css';
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {
    ChecklistDto,
    DestinationDto,
    destinationsApi,
    FilterGroupItemsDto,
    myTripChecklistApi,
    myTripsApi,
    publicApi,
    TripDestinationDto,
    TripDto
} from '../../api/api-wrapper'
import {MenuViewModelContext} from "../menu/menu";
import {getSubfilters} from "../../api/api-helpers";
import _flatten from "lodash-es/flatten";
import _cloneDeep from "lodash-es/cloneDeep"
import globalState from '../../global-state';
import {postbox} from "../../components/util/postbox";
import '../../pages/groups/filter';
import {createConfirmModal} from '../../components/elements/modal/modal';
import i18nextko from "../../components/util/i18nko";
import {isAllowedDestination} from "../../utils/utils";

class ViewModelContext extends Context {
    $parent: MenuViewModelContext;
    checklist: ChecklistDto;
    destinations: Array<DestinationDto>;
    groupItems: FilterGroupItemsDto;

}

class ViewModel {

    /**
     * Array with available destinations
     */
    public destinations: KnockoutObservableArray<DestinationDto>;

    /**
     * Selected trip destinations.
     */
    public selectedDestinations: KnockoutObservableArray<TripDestinationDto>;

    /**
     * Array with available filters for each group.
     */
    public filterGroupItems: KnockoutObservableArray<FilterGroupItemsDto>;

    /**
     * Selected filters map.
     * Key is the group id, value is an array with the selected filter ids.
     */
    public selectedFilters: Map<number, KnockoutObservableArray<number>>;

    /**
     * An validated observable that checks if all validations in this page are valid.
     */
    public validationStatus: KnockoutObservable<any>;

    /**
     * Stores checklist from template.
     */
    private checklist: ChecklistDto;

    /**
     * The current trip.
     */
    public trip: TripDto;

    constructor(ctx: ViewModelContext) {
        this.checklist = ctx.checklist;
        this.trip = globalState.currentTrip;

        this.destinations = ko.observableArray(ctx.destinations);
        // copy trip destinations array into new array before making it observable to avoid direct update of the trip
        this.selectedDestinations = ko.observableArray(this.trip.destinations.slice())
            .extend({required: true});

        // convert FilterGroupItemsDto into array of groups with corresponding items
        this.filterGroupItems = ko.observableArray(ctx.groupItems.groups
            .sort((g1, g2) => g1.order - g2.order)
            .map(group =>
                <FilterGroupItemsDto>{
                    groups: [
                        group
                    ],
                    items: getSubfilters(ctx.groupItems, group.id)
                }));

        // initialize selected filter items by group
        this.selectedFilters = new Map<number, KnockoutObservableArray<number>>();
        ctx.groupItems.groups.forEach(group => {
            this.selectedFilters.set(group.id, ko.observableArray<number>())
        });

        const flattenedSelectedFilters: KnockoutComputed<number[]> = ko.pureComputed(() => {
            return _flatten(Array.from(this.selectedFilters.values()).map(value => value()));
        });


        // set selected items for each group
        if (this.checklist) {
            this.checklist.createdFromFilters
                .map(itemId => parseInt(itemId))
                .forEach(itemId => {
                    const filterGroup = this.filterGroupItems().find(group =>
                        group.items.findIndex(item => item.id == itemId) > -1);

                    if (filterGroup) {
                        const selectedGroupFilter = this.selectedFilters.get(filterGroup.groups[0].id);
                        selectedGroupFilter.push(itemId);
                        const item = filterGroup.items.find(item => item.id == itemId);

                        // add item dependencies
                        if (item) {
                            item.dependency
                                .filter(dependencyId =>
                                    ctx.groupItems.items.findIndex(parentItem =>
                                        parentItem.id.toString() == dependencyId) > -1)
                                .forEach(parentItemId => selectedGroupFilter.push(parseInt(parentItemId)))
                        }
                    }
                })
        }


        this.validationStatus = ko.validatedObservable({
            selectedDestinations: this.selectedDestinations,
            selectedFilters: flattenedSelectedFilters
        });

        // bind this to ui event handlers
        this.selectDestination = this.selectDestination.bind(this);
        this.deselectDestination = this.deselectDestination.bind(this);
        this.resetEntry = this.resetEntry.bind(this);
        this.saveChecklist = this.saveChecklist.bind(this);
    }


    /**
     * Format a destination for the autocomplete dropdown.
     * @param destination
     */
    public formatDestination(destination: DestinationDto): string {
        return destination.name;
    }

    /**
     * Handler for selecting a destination.
     * Returns an empty string to reset the search field.
     * @param destination
     */
    public selectDestination(destination: DestinationDto): string {
        this.selectedDestinations.push(destination);
        return "";
    }

    /**
     * Handler for deselecting a destination.
     * @param destination
     */
    public deselectDestination(destination: DestinationDto): void {
        this.selectedDestinations.remove(destination);
    }


    public resetEntry(): void {
        const title = i18nextko.t('settings.reset.modal.title').peek();
        const text = i18nextko.t('settings.reset.modal.message').peek();
        const confirm = i18nextko.t('settings.reset.modal.confirm').peek();
        const cancel = i18nextko.t('settings.reset.modal.cancel').peek();
        const iconCssClass = "fo-icon fo-icon-sync";

        createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                globalState.loading(true);
                return myTripChecklistApi.uncheckAllTripChecklistItems(this.trip.id.toString(), {})
                    .then(() => {
                        globalState.loading(false);

                        return Router.update(`/reise/${this.trip.id}/checkliste`, {
                            push: true,
                            force: false
                        })
                    })
                    .catch(err => {
                        globalState.loading(false);
                        console.error('failed to reset checklist items', err);
                        postbox.addError('settings.errors.failedToResetItems');
                    });
            })
            .catch(() => {
                return false;
            });
    }

    public saveChecklist(): void {
        const errors = ko.validation.group(this);
        if (errors().length > 0) {
            errors.showAllMessages();
            postbox.addError('validation.failed');

        } else {
            globalState.loading(true);

            const trip = _cloneDeep(this.trip);
            // get selected trips that are not already added to the trip
            const addedDestinations = this.selectedDestinations()
                .filter(selectedDestination =>
                    !trip.destinations.some(tripDestination => tripDestination.id == selectedDestination.id))
                .map(destination => destination.id);
            trip.destinations = this.selectedDestinations()
                .map(destination => ({id: destination.id}));

            // first update destinations
            myTripsApi.updateTrip(trip.id.toString(), trip)
                .then(wrappedTrip => {
                    // update trip observable with saved destinations
                    const updatedTrip = wrappedTrip.data;
                    this.trip.destinations = updatedTrip.destinations;

                    return updatedTrip;
                })
                .then(updatedTrip => {
                    // get flattened array of selected filters
                    const checklistFilters = <ChecklistDto>{
                        selectedFilters: _flatten(
                            Array.from(this.selectedFilters.values()).map(filterItem => filterItem())),
                        selectedDestinations: addedDestinations
                    };

                    // update checklist filters
                    return myTripChecklistApi.updateTripChecklist(updatedTrip.id.toString(), checklistFilters);
                })
                .then(() => {
                    globalState.loading(false);
                    return Router.update(`/reise/${trip.id}/checkliste`, {
                        push: true,
                        force: false
                    })
                })
                .catch(err => {
                    globalState.loading(false);
                    console.error('failed to save checklist settings', err);
                    postbox.addError('settings.errors.failedToSaveSettings');
                });
        }
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./settings.html'),
    componentName: "settings",
    loader: (ctx: ViewModelContext) =>
        // load filters and destinations
        Promise.all([
            publicApi.getFilters().then(result => ctx.groupItems = result.data),
            destinationsApi.getDestinations()
                .then(result =>
                    ctx.destinations = result.data.filter(destination =>
                        isAllowedDestination(destination))
                )

        ]).then(() => {
            return myTripChecklistApi.getTripChecklist(globalState.currentTrip.id.toString()).then(result => {
                ctx.checklist = result.data;
                return null;
            });
        })
};
